import { observer } from 'mobx-react-lite'
import { Alert, ButtonCopy, Scrollbar, TextFieldWithLabel, Typography } from 'shared/ui'
import { REFRESH_TOKEN_DOCS_URL } from 'shared/constants/links'
import { type AccessTokensStore } from 'pages/settings/pages/developer/pages/accessTokens/store/accessTokensStore'
import { ScopeBlock } from 'pages/settings/pages/developer/ui/ScopeBlock/ScopeBlock'
import styles from './styles.module.scss'

type INewTokenModalContentProps = {
  store: AccessTokensStore
}

export const NewTokenModalContent = observer(({ store }: INewTokenModalContentProps) => {
  const created = store.createdTokenResponse

  return (
    <Scrollbar autoHeight autoHeightMax={created ? '596px' : '524px'}>
      <div className={styles.fields} aria-label='New token form'>
        {store.createTokenError && (
          <Alert
            item={{
              type: 'error',
              desc: 'Failed to create token. Please try again.',
            }}
            marginBottom={16}
            onClose={store.clearCreateTokenError}
          />
        )}

        <TextFieldWithLabel
          labelProps={{
            label: {
              text: 'Name',
            },
          }}
          bottomLabelProps={{
            label: {
              text: (
                <>
                  Note that all new Personal Access Tokens created now expire one hour after being
                  received. You can use the
                  <a
                    href={REFRESH_TOKEN_DOCS_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={styles.docLink}
                  >
                    new API method
                  </a>
                  to update these tokens.
                </>
              ),
              textClassName: styles.noteText,
              className: styles.note,
            },
            marginTop: 16,
          }}
          limit={191}
          errorMessage={store.newTokenNameError}
          textFieldProps={{
            value: store.newTokenName,
            onChange: store.setNewTokenName,
            onBlur: store.validateNewTokenNameBlur,
            disabled: Boolean(created),
            InputProps: {
              placeholder: 'Enter token name',
            },
          }}
          marginBottom={'8px'}
        />
        <ScopeBlock
          scopes={store.allScopes}
          toggleScope={store.toggleScope}
          selectedScopes={store.selectedScopes}
          disabled={Boolean(created)}
        />

        {created && (
          <div className={styles.tokenContainer}>
            <Alert
              item={{
                type: 'success',
                title: 'Your new token has been created.',
                desc: "Save this token to your computer, it won't be shown again.",
              }}
              marginTop={10}
            />
            <div className={styles.tokenRow}>
              <Typography
                variant='body-md-regular'
                tag='div'
                ellipsis
                width='100%'
                ariaLabel='NewTokenAccessToken'
              >
                {created.accessToken}
              </Typography>
              <ButtonCopy
                value={created.accessToken}
                buttonProps={{ typeBtn: 'contained', contained: 'primary' }}
              />
            </div>
          </div>
        )}
      </div>
    </Scrollbar>
  )
})
